import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import type { RootState } from '@/store';

import { campaignsApi } from '@/api';
import type { ICampaignsRaw, IThunkAPI } from '@/store/types';
import type { ICampaignsState, ICampaignsSummary } from '@/api/types';
import { KnownErrors } from '@/store/enums';

export const initialState: ICampaignsState = {
  campaignsList: [],
  campaigns: null,
  campaignsKeyValue: [],
  isCampaignsError: false,
  isCampaignsSuccess: false,
  isCampaignsFetching: false,
  selectedCampaignId: 0,
};

export const getCampaignsStatus = createAsyncThunk<ICampaignsRaw[], number | null | undefined, IThunkAPI>(
  'get/campaigns/clientId',
  async (clientID, thunkAPI) => {
    try {
      const response = await campaignsApi.getCampaignsStatus(clientID);
      let data = response.data;
      if (response.data === KnownErrors.ItemNotFound) {
        data = [];
      }
      return data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getcampaignsById = createAsyncThunk<ICampaignsSummary[], number | null | undefined, IThunkAPI>(
  'get/campaigns/campaignsId',
  async (id, thunkAPI) => {
    try {
      const { data } = await campaignsApi.getCampaignsById(id);
      const campaignList = [...new Set([].concat(...data.map((o: { campaigns: ICampaignsSummary }) => o.campaigns)))];
      return campaignList;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const campaignsSlice = createSlice({
  name: 'campaigns',
  initialState,
  reducers: {
    clearcampaignss(state) {
      state.campaignsList = [];
    },
    updateSelectedCampaignId(state, { payload }) {
      state.selectedCampaignId = payload;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getCampaignsStatus.fulfilled, (state, action) => {
        if (action.payload && action.payload?.length > 0) {
          state.campaignsList = action.payload;
        } else {
          state.campaignsList = [];
        }
        state.isCampaignsSuccess = true;
        state.isCampaignsError = false;
        state.isCampaignsFetching = false;
      })
      .addCase(getCampaignsStatus.rejected, state => {
        state.isCampaignsError = true;
        state.isCampaignsSuccess = false;
        state.isCampaignsFetching = false;
      })
      .addCase(getCampaignsStatus.pending, state => {
        state.isCampaignsFetching = true;
      })
      .addCase(getcampaignsById.fulfilled, (state, action) => {
        const sortedList = action.payload.sort((a: ICampaignsSummary, b: ICampaignsSummary) => {
          const fa = a.title.toLowerCase(),
            fb = b.title.toLowerCase();

          if (fa < fb) {
            return -1;
          }
          if (fa > fb) {
            return 1;
          }
          return 0;
        });
        state.campaignsKeyValue = sortedList;
      });
  },
});

// actions
export const { clearcampaignss, updateSelectedCampaignId } = campaignsSlice.actions;

// selectors
export const campaigns = (state: RootState): ICampaignsState['campaigns'] => state.campaigns.campaigns;
export const isCampaignsFetching = (state: RootState): boolean => state.campaigns.isCampaignsFetching;
export const campaignGigAndCampaignName = (state: RootState): string => {
  let selectedGigAndcampaign = '';
  state.campaigns.campaignsList.filter(campaign => {
    if (campaign.campaignId === state.campaigns.selectedCampaignId) {
      selectedGigAndcampaign = `${campaign?.gigName || ''} - ${campaign?.campaignName || ''}`;
    }
  });
  return selectedGigAndcampaign;
};

export const campaignGigName = (state: RootState): string => {
  const selectedCampaign = state.campaigns.campaignsList.find(
    campaign => campaign.campaignId === state.campaigns.selectedCampaignId,
  );
  return selectedCampaign?.gigName || '';
};

export default campaignsSlice.reducer;
